import React, { useState, useEffect } from 'react';
import { Box, Card, Container, Grid, Chip, Dialog, DialogTitle, DialogContent, TextField, DialogActions, Button } from '@mui/material';
import { useAuth } from '../../Components/Contexts/authContext';
import './historical_withraw.css';
import Spinner from '../Loading/spiner'; 
import Menu from '../Menu/menu';
import Pagination from './pagination';

const HistoricalWithdraw = () => {
  const [retiros, setRetiros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { token, role } = useAuth();
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [filterValueStart, setFilterValueStart] = useState('');
  const [filterValueEnd, setFilterValueEnd] = useState('');
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const itemsPerPage = 10; // Number of items per page
  const [appliedFilter, setAppliedFilter] = useState('');

  const onPageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const applyFilter = () => {
    let url;
  
    // Verificar si hay un filtro seleccionado
    if (selectedFilter === 'fecha' || selectedFilter === 'monto') {
      if (filterValueStart && filterValueEnd) {
        url = getFilterUrl(selectedFilter, filterValueStart, filterValueEnd);
      } else {
        // Si los valores de inicio y fin están vacíos, usar la URL predeterminada
        url = getFilterUrl(); 
      }
    } else if (selectedFilter === 'empresa' || selectedFilter === 'cedula') {
      if (filterValue) {
        url = getFilterUrl(selectedFilter, null, null, filterValue);
      } else {
        // Si no hay valor para empresa o cédula, usar la URL predeterminada
        url = getFilterUrl();
      }
    } else {
      // Si no hay filtro seleccionado, usar la URL predeterminada
      url = getFilterUrl();
    }
    
    // Llamar a la función fetchRetiros con la URL construida
    fetchRetiros(url);

    // Establecer el estado de filtro aplicado
    setAppliedFilter(selectedFilter);
    setIsFilterApplied(true);
  
    // Restablecer el estado de los filtros
    setOpenFilterDialog(false);
    setFilterValue(''); 
    setFilterValueStart('');
    setFilterValueEnd('');
    setSelectedFilter(''); // Restablecer el filtro seleccionado
  };
  
  const handleClearFilters = () => {
    // Limpiar el estado de los filtros
    setFilterValue(''); 
    setFilterValueStart('');
    setFilterValueEnd('');
    setSelectedFilter(''); 
  
    // Llamar a fetchRetiros con la URL predeterminada para obtener todos los registros
    const url = getFilterUrl();
    fetchRetiros(url);

    // Set isFilterApplied to false AFTER fetching data to trigger a re-render
    setIsFilterApplied(false);
  };
  
  // Función auxiliar para obtener la URL del filtro con parámetros
  const getFilterUrl = (filter, filterValueStart, filterValueEnd, filterValue) => {
    switch (filter) {
      case 'fecha':
        return `https://z3tr309yf9.execute-api.us-east-1.amazonaws.com/get-adelantos-by-date/${filterValueStart}/${filterValueEnd}`;
      case 'monto':
        return `https://z3tr309yf9.execute-api.us-east-1.amazonaws.com/get-adelantos-by-amount/${filterValueStart}/${filterValueEnd}`;
      case 'empresa':
        return `https://z3tr309yf9.execute-api.us-east-1.amazonaws.com/get-adelantos-by-company/${filterValue}`;
      case 'cedula':
        return `https://z3tr309yf9.execute-api.us-east-1.amazonaws.com/get-adelantos-by-ci/${filterValue}`;
      default:
        return role === 'admin'
          ? 'https://z3tr309yf9.execute-api.us-east-1.amazonaws.com/get-admin-adelantos'
          : 'https://z3tr309yf9.execute-api.us-east-1.amazonaws.com/get-user-adelantos';
    }
  };

  useEffect(() => {
    // Llamar a la URL predeterminada al cargar la página
    const url = getFilterUrl(); // Llamada a getFilterUrl para obtener la URL predeterminada
    fetchRetiros(url);
  }, [token, role]);
  
  const fetchRetiros = async (url) => {
    setLoading(true);
    setError(null);
    try {
      const options = {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      };
  
      const response = await fetch(url, options);
  
      if (response.ok) {
        const data = await response.json();
        setRetiros(data);
        setPage(1);
      } else {
        throw new Error('Error al obtener los retiros.');
      }
    } catch (error) {
      console.error('Error fetching retiros:', error);
      setError('Error al cargar los retiros.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterClick = (filter) => {
    setSelectedFilter(filter);
    setOpenFilterDialog(true);
  };

  if (loading) {
    return <Spinner />;
  }

    // Calculate the current items to display based on the page
    const indexOfLastItem = page * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = retiros.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(retiros.length / itemsPerPage);
  
    // Handle page change
    const handleNextPage = () => {
      if (page < totalPages) {
        setPage(prevPage => prevPage + 1);
      }
    };
  
    const handlePreviousPage = () => {
      if (page > 1) {
        setPage(prevPage => prevPage - 1);
      }
    };
  
  return (
    <div className="historical-container">
      <header className="historical-withdraw-header font-bold">
        <h1>Historial de Retiros</h1>
      </header>

      <Container maxWidth="sm" className="historical-content">
        <Box sx={{ mt: 2 }}> 
          <div className="filters-container">
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item>
                    <Chip 
                      label="Fecha" 
                      className="custom-chip"
                      sx={{ 
                        backgroundColor: appliedFilter === 'fecha' ? '#A4D6A0' : 'rgba(164, 214, 160, 0.50)', 
                        color: '#000', 
                        fontWeight: 500,
                        '&:hover': {
                          backgroundColor: '#A4D6A0',
                        },
                      }} 
                      onClick={() => handleFilterClick('fecha')} 
                    />
                  </Grid>
                  <Grid item>
                    <Chip 
                      label="Monto" 
                      className="custom-chip"
                      sx={{ 
                        backgroundColor: appliedFilter === 'monto' ? '#A4D6A0' : 'rgba(164, 214, 160, 0.50)', 
                        color: '#000',
                        fontWeight: 500, 
                        '&:hover': {
                          backgroundColor: '#A4D6A0',
                        },
                      }} 
                      onClick={() => handleFilterClick('monto')} 
                    />
                  </Grid>
                  {localStorage.getItem("role") === 'admin' && (
                    <>
                      <Grid item>
                        <Chip 
                          label="Empresa" 
                          className="custom-chip"
                          sx={{ 
                            backgroundColor: appliedFilter === 'empresa' ? '#A4D6A0' : 'rgba(164, 214, 160, 0.50)', 
                            color: '#000',
                            fontWeight: 500, 
                            '&:hover': {
                              backgroundColor: '#A4D6A0',
                            },
                          }} 
                          onClick={() => handleFilterClick('empresa')} 
                        />
                      </Grid>
                      <Grid item>
                        <Chip 
                          label="Cédula" 
                          className="custom-chip"
                          sx={{ 
                            backgroundColor: appliedFilter === 'cedula' ? '#A4D6A0' : 'rgba(164, 214, 160, 0.50)', 
                            color: '#000',
                            fontWeight: 500, 
                            '&:hover': {
                              backgroundColor: '#A4D6A0',
                            },
                          }} 
                          onClick={() => handleFilterClick('cedula')} 
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Grid>
              <Grid item xs={2} display="flex" justifyContent="flex-end">
                {isFilterApplied && (
                  <Chip 
                    label="Limpiar filtros"
                    onClick={handleClearFilters} 
                    sx={{ 
                      backgroundColor: '#ffff',
                      color: '#000',
                      border: '1px solid #A4D6A0',
                      minWidth: '120px',
                      fontWeight: 'bold',
                      '&:hover': {
                        backgroundColor: '#A4D6A0',
                      }
                    }} 
                  />
                )}
              </Grid>
            </Grid>
          </div>
          <Box className="retiro-list" mt={0}>
            {retiros.length === 0 ? (
              <Box
                sx={{
                  textAlign: 'center',
                  color: '#B0B0B0',
                  fontStyle: 'italic',
                  mt: 4,
                }}
              >
                ¡No tienes ningun adelanto aún! <br />
                Aqui podrás ver todos los adelantos que has realizado.
              </Box>
            ) : (
              currentItems.map((retiro, index) => (
                <Card key={index} className="retiro-card" sx={{ boxShadow: '0 6px 12px rgba(0, 0, 0, 0.25)' }}>
                  <div className="retiro-info">
                    <div className="retiro-field">
                      <p><span className="font-bold">Importe:</span></p>
                      <p>${retiro.amount}</p>
                    </div>
                    <div className="retiro-field">
                      <p><span className="font-bold">Id de transacción:</span></p>
                      <p>{retiro.id_transaction}</p>
                    </div>
                    <div className="retiro-field">
                      <p><span className="font-bold">Fecha:</span></p>
                      <p>{retiro.date_of_transaction}</p>
                    </div>
                    {localStorage.getItem("role") === 'admin' && (
                      <>
                        <div className="retiro-field">
                          <p><span className="font-bold">Empresa:</span></p>
                          <p>{retiro.company_name}</p>
                        </div>
                        <div className="retiro-field">
                          <p><span className="font-bold">Cédula:</span></p>
                          <p>{retiro.user.ci}</p>
                        </div>
                      </>
                    )}
                  </div>
                </Card>
              ))
            )}
          </Box>
        </Box>
      </Container>
      <Pagination page={page} totalPages={totalPages} onPageChange={onPageChange} />
      <Dialog 
        open={openFilterDialog} 
        onClose={() => setOpenFilterDialog(false)}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: '20px',
            padding: '20px',
          },
        }}
      >
        <DialogTitle>Filtrar por {selectedFilter}</DialogTitle>
        <DialogContent>
          {selectedFilter === 'fecha' ? (
            <>
              <TextField
                autoFocus
                margin="dense"
                label="Inicio Fecha"
                type="date"
                fullWidth
                value={filterValueStart}
                onChange={(e) => setFilterValueStart(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{ mb: 2 }}
              />
              <TextField
                margin="dense"
                label="Fin Fecha"
                type="date"
                fullWidth
                value={filterValueEnd}
                onChange={(e) => setFilterValueEnd(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </>
          ) : selectedFilter === 'monto' ? (
            <>
              <TextField
                autoFocus
                margin="dense"
                label="Monto Inicio"
                type="number"
                fullWidth
                value={filterValueStart}
                onChange={(e) => setFilterValueStart(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                margin="dense"
                label="Monto Fin"
                type="number"
                fullWidth
                value={filterValueEnd}
                onChange={(e) => setFilterValueEnd(e.target.value)}
              />
            </>
          ) : (
            <TextField
              autoFocus
              margin="dense"
              label={`Ingrese ${selectedFilter}`}
              fullWidth
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenFilterDialog(false)} 
            sx={{ backgroundColor: '#fff', color: '#000' , border: '1px solid #A4D6A0'}} 
          >
            Cancelar
          </Button>
          <Button 
            onClick={applyFilter} 
            sx={{ backgroundColor: '#A4D6A0', color: '#000', ml: 1 , border: '1px solid #A4D6A0' }}  // textTransform: 'none' si se quiere quitar mayúsculas
          >
            Aplicar
          </Button>
        </DialogActions>
      </Dialog>
      <div className="menu-container">
        <Menu />
      </div>
    </div>
  );
};

export default HistoricalWithdraw;
